/**
 * Leaderboard Filters
 * Filter controls for the leaderboard
 */

import type { BetsType, SortBy, TimePeriod } from '@parlay-picker/shared/hooks';
import { PROP_TYPE_FILTER_OPTIONS } from '@parlay-picker/shared/propTypes';
import type { ReactElement } from 'react';

// =============================================================================
// Types
// =============================================================================

interface LeaderboardFiltersProps {
  timePeriod: TimePeriod;
  onTimePeriodChange: (period: TimePeriod) => void;
  betsType: BetsType;
  onBetsTypeChange: (type: BetsType) => void;
  propType: string | undefined;
  onPropTypeChange: (propType: string | undefined) => void;
  verifiedOnly: boolean;
  onVerifiedOnlyChange: (verified: boolean) => void;
  followingOnly: boolean;
  onFollowingOnlyChange: (following: boolean) => void;
  sortBy: SortBy;
  onSortByChange: (sort: SortBy) => void;
  minBets: number;
  onMinBetsChange: (minBets: number) => void;
}

// =============================================================================
// Component
// =============================================================================

export function LeaderboardFilters({
  timePeriod,
  onTimePeriodChange,
  betsType,
  onBetsTypeChange,
  propType,
  onPropTypeChange,
  verifiedOnly,
  onVerifiedOnlyChange,
  followingOnly,
  onFollowingOnlyChange,
  sortBy,
  onSortByChange,
  minBets,
  onMinBetsChange,
}: LeaderboardFiltersProps): ReactElement {
  return (
    <div data-testid="leaderboard-filters" className="bg-theme-surface/50 rounded-lg p-4 space-y-4">
      <div className="grid grid-cols-1 @3xl:grid-cols-2 @5xl:grid-cols-3 @7xl:grid-cols-5 gap-4">
        {/* Time Period */}
        <div>
          <label className="block text-sm font-medium text-theme-text-secondary mb-2">
            Time Period
          </label>
          <select
            value={timePeriod}
            onChange={(e) => onTimePeriodChange(e.target.value as TimePeriod)}
            className="w-full bg-theme-surface border border-theme-border rounded-lg px-3 py-2 text-theme-text focus:outline-none focus:ring-2 focus:ring-theme-primary"
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="season">Current Season</option>
            <option value="alltime">All Time</option>
          </select>
        </div>

        {/* Bets Type */}
        <div>
          <label className="block text-sm font-medium text-theme-text-secondary mb-2">
            Bets Type
          </label>
          <select
            value={betsType}
            onChange={(e) => onBetsTypeChange(e.target.value as BetsType)}
            className="w-full bg-theme-surface border border-theme-border rounded-lg px-3 py-2 text-theme-text focus:outline-none focus:ring-2 focus:ring-theme-primary"
          >
            <option value="all">All Tracked</option>
            <option value="actual">Actual Bets Only</option>
          </select>
        </div>

        {/* Prop Type */}
        <div>
          <label className="block text-sm font-medium text-theme-text-secondary mb-2">
            Prop Type
          </label>
          <select
            value={propType || ''}
            onChange={(e) => onPropTypeChange(e.target.value || undefined)}
            className="w-full bg-theme-surface border border-theme-border rounded-lg px-3 py-2 text-theme-text focus:outline-none focus:ring-2 focus:ring-theme-primary"
          >
            {PROP_TYPE_FILTER_OPTIONS.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Sort By */}
        <div>
          <label className="block text-sm font-medium text-theme-text-secondary mb-2">
            Sort By
          </label>
          <select
            value={sortBy}
            onChange={(e) => onSortByChange(e.target.value as SortBy)}
            className="w-full bg-theme-surface border border-theme-border rounded-lg px-3 py-2 text-theme-text focus:outline-none focus:ring-2 focus:ring-theme-primary"
          >
            <optgroup label="Fair Ranking (Adjusted)">
              <option value="roi">ROI (Adjusted)</option>
              <option value="winRate">Win Rate (Adjusted)</option>
            </optgroup>
            <optgroup label="Pure Performance (Raw)">
              <option value="rawRoi">ROI (Raw)</option>
              <option value="rawWinRate">Win Rate (Raw)</option>
            </optgroup>
            <optgroup label="Other">
              <option value="profit">Total Profit</option>
              <option value="streak">Current Streak</option>
            </optgroup>
          </select>
        </div>

        {/* Min Bets */}
        <div>
          <label className="block text-sm font-medium text-theme-text-secondary mb-2">
            Min Bets: {minBets}
          </label>
          <input
            type="range"
            min="5"
            max="100"
            step="5"
            value={minBets}
            onChange={(e) => onMinBetsChange(parseInt(e.target.value, 10))}
            className="w-full h-2 bg-theme-surface rounded-lg appearance-none cursor-pointer accent-theme-primary"
          />
          <div className="flex justify-between text-xs text-theme-text-tertiary mt-1">
            <span>5</span>
            <span>100</span>
          </div>
        </div>
      </div>

      {/* Verified Only Checkbox */}
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="verified-only"
          checked={verifiedOnly}
          onChange={(e) => onVerifiedOnlyChange(e.target.checked)}
          className="w-4 h-4 text-theme-primary bg-theme-surface border-theme-border rounded focus:ring-theme-primary focus:ring-2"
        />
        <label htmlFor="verified-only" className="text-sm text-theme-text-secondary cursor-pointer">
          Show verified users only (with linked sportsbook accounts)
        </label>
      </div>

      {/* Following Only Checkbox */}
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="following-only"
          checked={followingOnly}
          onChange={(e) => onFollowingOnlyChange(e.target.checked)}
          className="w-4 h-4 text-theme-primary bg-theme-surface border-theme-border rounded focus:ring-theme-primary focus:ring-2"
        />
        <label
          htmlFor="following-only"
          className="text-sm text-theme-text-secondary cursor-pointer"
        >
          Show only users I follow
        </label>
      </div>
    </div>
  );
}
